import { useEffect, useState } from 'react';
import {
    FiAlertCircle,
    FiCreditCard,
    FiDollarSign,
    FiTrendingUp,
    FiUsers
} from 'react-icons/fi';
import { adminApi } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';
import { AdminMetrics } from './AdminMetrics';

interface AdminKpis {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    suspendedUsers: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    trialSubscriptions: number;
    paidActiveSubscriptions: number; // ACTIVE em plano pago (preço > 0)
    paidMonthlySubscriptions: number;
    paidYearlySubscriptions: number;
    testSubscriptions: number; // TRIAL ou plano gratuito
    expiringSoon: number; // expirando em 30 dias
    expiredSubscriptions: number;
    mrr: number; // receita mensal recorrente estimada
    arr: number; // receita anual recorrente estimada (mrr * 12)
}

export function AdminDashboard() {
    const [kpis, setKpis] = useState<AdminKpis | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.getDashboardKpis()
            .then(setKpis)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const formatCurrency = (value: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Dashboard Administrativo</h2>
                    <p>Visão geral da plataforma LTQ</p>
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <>
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.users}`}><FiUsers size={20} /></div>
                            <div className={styles.label}>Total de Usuários</div>
                            <div className={styles.value}>{kpis?.totalUsers ?? 0}</div>
                            <div className={styles.subLabel}>
                                <span className={styles.activeCount}>{kpis?.activeUsers ?? 0} ativos</span>
                                {(kpis?.inactiveUsers ?? 0) > 0 && (
                                    <span className={styles.inactiveCount}> / {kpis?.inactiveUsers} inativos</span>
                                )}
                                {(kpis?.suspendedUsers ?? 0) > 0 && (
                                    <span className={styles.inactiveCount}> / {kpis?.suspendedUsers} suspensos</span>
                                )}
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.revenue}`}><FiDollarSign size={20} /></div>
                            <div className={styles.label}>MRR Estimado</div>
                            <div className={styles.value}>{formatCurrency(kpis?.mrr ?? 0)}</div>
                            <div className={styles.subLabel}>{kpis?.paidActiveSubscriptions ?? 0} assinaturas pagas</div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.revenue}`}><FiTrendingUp size={20} /></div>
                            <div className={styles.label}>ARR Estimado</div>
                            <div className={styles.value}>{formatCurrency(kpis?.arr ?? 0)}</div>
                            <div className={styles.subLabel}>MRR × 12</div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.active}`}><FiCreditCard size={20} /></div>
                            <div className={styles.label}>Assinaturas Ativas</div>
                            <div className={styles.value}>{kpis?.paidActiveSubscriptions ?? 0}</div>
                            <div className={styles.subLabel}>
                                {kpis?.paidMonthlySubscriptions ?? 0} mensais / {kpis?.paidYearlySubscriptions ?? 0} anuais
                            </div>
                            <div className={styles.subLabel}>
                                {kpis?.testSubscriptions ?? 0} em teste ou plano gratuito
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.icon} ${styles.plans}`}><FiAlertCircle size={20} /></div>
                            <div className={styles.label}>Expirando (30d)</div>
                            <div className={styles.value}>{kpis?.expiringSoon ?? 0}</div>
                            <div className={styles.subLabel}>
                                {kpis?.expiredSubscriptions ?? 0} já expiradas
                            </div>
                        </div>
                    </div>

                    <AdminMetrics />
                </>
            )}
        </AdminLayout>
    );
}
